/**
 * IntegrationApps Component
 * Clean grid display of integration apps with OAuth connection support
 */

import React, { useState, useEffect } from 'react';
import { Check, ExternalLink, Loader, User, X } from 'lucide-react';
import { 
  JiraIcon, ConfluenceIcon, GitHubIcon, 
  AzureDevOpsIcon, SlackIcon, TeamsIcon,
} from '../../common/BrandIcons';
import api, { MCPOAuthStatus, IntegrationSummary } from '../../../services/api';
import styles from './IntegrationApps.module.css';

export interface IntegrationApp {
  id: string;
  name: string;
  icon: React.ReactNode;
  provider: string;
  authType: 'oauth' | 'pat' | 'api-key';
  description?: string;
}

const DEFAULT_APPS: IntegrationApp[] = [
  { id: 'github', name: 'GitHub', icon: <GitHubIcon size={32} />, provider: 'github', authType: 'oauth', description: 'Code repositories & CI/CD' },
  { id: 'jira', name: 'Jira', icon: <JiraIcon size={32} />, provider: 'jira', authType: 'oauth', description: 'Issue tracking' },
  { id: 'confluence', name: 'Confluence', icon: <ConfluenceIcon size={32} />, provider: 'confluence', authType: 'oauth', description: 'Documentation' },
  { id: 'slack', name: 'Slack', icon: <SlackIcon size={32} />, provider: 'slack', authType: 'oauth', description: 'Team messaging' },
  { id: 'azure-devops', name: 'Azure DevOps', icon: <AzureDevOpsIcon size={32} />, provider: 'azure-devops', authType: 'pat', description: 'Work items & pipelines' },
  { id: 'teams', name: 'Teams', icon: <TeamsIcon size={32} />, provider: 'teams', authType: 'oauth', description: 'Microsoft Teams' },
];

interface IntegrationAppsProps {
  apps?: IntegrationApp[];
  selectedApps?: string[];
  onAppSelect?: (appId: string, selected: boolean) => void;
  onAppConnect?: (app: IntegrationApp) => void;
  showConnectionStatus?: boolean;
}

export const IntegrationApps: React.FC<IntegrationAppsProps> = ({
  apps = DEFAULT_APPS,
  selectedApps = [],
  onAppSelect,
  onAppConnect,
  showConnectionStatus = true,
}) => {
  const [connectionStatus, setConnectionStatus] = useState<Record<string, MCPOAuthStatus>>({});
  const [integrations, setIntegrations] = useState<Record<string, IntegrationSummary>>({});
  const [loadingApp, setLoadingApp] = useState<string | null>(null);
  const [showPatModal, setShowPatModal] = useState<IntegrationApp | null>(null);
  const [showContextMenu, setShowContextMenu] = useState<{ app: IntegrationApp; x: number; y: number } | null>(null);
  const [patValue, setPatValue] = useState('');
  const [orgValue, setOrgValue] = useState('');

  // Check connection status for OAuth apps
  useEffect(() => {
    if (showConnectionStatus) {
      loadIntegrations();
    }
  }, [showConnectionStatus]);

  const loadIntegrations = async () => {
    try {
      // Load all integrations from the database
      const response = await api.getIntegrations();
      if (response.success && response.data) {
        const integrationMap: Record<string, IntegrationSummary> = {};
        response.data.forEach((integration) => {
          integrationMap[integration.provider] = integration;
        });
        setIntegrations(integrationMap);
        
        // Also update connection status for backward compatibility
        const statusMap: Record<string, MCPOAuthStatus> = {};
        response.data.forEach((integration) => {
          statusMap[integration.provider] = {
            connected: integration.connected,
            provider: integration.provider,
            accountInfo: integration.accountInfo,
          };
        });
        setConnectionStatus(statusMap);
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
      // Fall back to checking individual status
      checkConnectionStatus();
    }
  };

  const checkConnectionStatus = async () => {
    const oauthApps = apps.filter(app => app.authType === 'oauth');
    const statusPromises = oauthApps.map(async (app) => {
      try {
        const response = await api.getMCPOAuthStatus(app.provider);
        if (response.success && response.data) {
          return { provider: app.provider, status: response.data };
        }
        return { provider: app.provider, status: { connected: false, provider: app.provider } as MCPOAuthStatus };
      } catch {
        return { provider: app.provider, status: { connected: false, provider: app.provider } as MCPOAuthStatus };
      }
    });

    const results = await Promise.all(statusPromises);
    const statusMap: Record<string, MCPOAuthStatus> = {};
    results.forEach(({ provider, status }) => {
      statusMap[provider] = status;
    });
    setConnectionStatus(statusMap);
  };

  // IMPORTANT: This function MUST be synchronous to preserve user gesture for popup
  const handleAppClick = (app: IntegrationApp) => {
    const isConnected = connectionStatus[app.provider]?.connected;
    
    if (isConnected) {
      // If connected, toggle selection
      if (onAppSelect) {
        const isSelected = selectedApps.includes(app.id);
        onAppSelect(app.id, !isSelected);
      }
    } else {
      // If not connected, initiate connection
      if (app.authType === 'pat') {
        setShowPatModal(app);
      } else {
        // Call synchronously - DO NOT use await here!
        initiateOAuth(app);
      }
    }
  };

  // Listen for OAuth postMessage from popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'oauth-success') {
        console.log('OAuth success for:', event.data.provider);
        loadIntegrations();
        setLoadingApp(null);
      } else if (event.data?.type === 'oauth-error') {
        console.error('OAuth error:', event.data.error);
        setLoadingApp(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const initiateOAuth = (app: IntegrationApp) => {
    setLoadingApp(app.id);
    
    // Get OAuth URL synchronously
    const url = api.getMCPOAuthUrl(app.provider);
    if (!url) {
      console.error('No OAuth URL for provider:', app.provider);
      setLoadingApp(null);
      return;
    }
    
    // Create a temporary anchor element and click it
    // This is a more reliable way to open popups in modern browsers
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Set up a timer to refresh integrations after OAuth completes
    // Since we can't reliably track the popup, we'll poll
    const startTime = Date.now();
    const maxWaitTime = 5 * 60 * 1000; // 5 minutes max
    
    const checkConnection = setInterval(async () => {
      try {
        const response = await api.getMCPOAuthStatus(app.provider);
        if (response.success && response.data?.connected) {
          clearInterval(checkConnection);
          setLoadingApp(null);
          loadIntegrations();
        } else if (Date.now() - startTime > maxWaitTime) {
          clearInterval(checkConnection);
          setLoadingApp(null);
        }
      } catch (e) {
        // Keep polling
      }
    }, 2000);
  };

  const handlePatSubmit = async () => {
    if (!showPatModal || !patValue) return;
    
    setLoadingApp(showPatModal.id);
    try {
      await api.authenticateAzureDevOps(orgValue, patValue);
      // Refresh integrations from database
      await loadIntegrations();
      setShowPatModal(null);
      setPatValue('');
      setOrgValue('');
    } catch (error) {
      console.error('Failed to authenticate:', error);
    } finally {
      setLoadingApp(null);
    }
  };

  const handleDisconnect = async (app: IntegrationApp) => {
    setLoadingApp(app.id);
    setShowContextMenu(null);
    try {
      await api.disconnectIntegration(app.provider);
      // Refresh integrations from database
      await loadIntegrations();
    } catch (error) {
      console.error('Failed to disconnect:', error);
    } finally {
      setLoadingApp(null);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, app: IntegrationApp) => {
    e.preventDefault();
    if (isAppConnected(app)) {
      setShowContextMenu({ app, x: e.clientX, y: e.clientY });
    }
  };

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => setShowContextMenu(null);
    if (showContextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [showContextMenu]);

  const isAppConnected = (app: IntegrationApp): boolean => {
    return connectionStatus[app.provider]?.connected || false;
  };

  const isAppSelected = (app: IntegrationApp): boolean => {
    return selectedApps.includes(app.id);
  };

  const getAccountInfo = (app: IntegrationApp) => {
    const status = connectionStatus[app.provider];
    const integration = integrations[app.provider];
    return status?.accountInfo || integration?.accountInfo;
  };

  return (
    <div className={styles.container}>
      <div className={styles.appsGrid}>
        {apps.map((app) => {
          const connected = isAppConnected(app);
          const selected = isAppSelected(app);
          const loading = loadingApp === app.id;
          const accountInfo = getAccountInfo(app);

          return (
            <button
              key={app.id}
              className={`${styles.appCard} ${connected ? styles.connected : ''} ${selected ? styles.selected : ''}`}
              onClick={() => handleAppClick(app)}
              onContextMenu={(e) => handleContextMenu(e, app)}
              disabled={loading}
              type="button"
              title={connected && accountInfo ? `Connected as ${accountInfo.name || accountInfo.email || 'User'} (Right-click to disconnect)` : app.description}
            >
              {loading && (
                <div className={styles.loadingOverlay}>
                  <Loader className={styles.spinner} size={20} />
                </div>
              )}
              
              <span className={styles.appIcon}>{app.icon}</span>
              <span className={styles.appName}>{app.name}</span>
              
              {connected && (
                <span className={styles.connectedBadge}>
                  <Check size={12} />
                </span>
              )}
              
              {connected && accountInfo && (
                <span className={styles.accountInfo}>
                  {accountInfo.avatarUrl ? (
                    <img 
                      src={accountInfo.avatarUrl} 
                      alt={accountInfo.name || 'User'} 
                      className={styles.avatar}
                    />
                  ) : (
                    <User size={10} className={styles.userIcon} />
                  )}
                  <span className={styles.accountName}>
                    {accountInfo.siteName || accountInfo.organization || accountInfo.name || accountInfo.email?.split('@')[0] || 'Connected'}
                  </span>
                </span>
              )}
              
              {!connected && (
                <span className={styles.connectHint}>
                  <ExternalLink size={10} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* PAT Modal for Azure DevOps */}
      {showPatModal && (
        <div className={styles.modalOverlay} onClick={() => setShowPatModal(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3>Connect {showPatModal.name}</h3>
            <p>Enter your organization and Personal Access Token</p>
            
            <div className={styles.formGroup}>
              <label>Organization</label>
              <input
                type="text"
                value={orgValue}
                onChange={e => setOrgValue(e.target.value)}
                placeholder="your-organization"
              />
            </div>
            
            <div className={styles.formGroup}>
              <label>Personal Access Token</label>
              <input
                type="password"
                value={patValue}
                onChange={e => setPatValue(e.target.value)}
                placeholder="Enter your PAT"
              />
            </div>
            
            <div className={styles.modalActions}>
              <button 
                className={styles.cancelBtn}
                onClick={() => setShowPatModal(null)}
                type="button"
              >
                Cancel
              </button>
              <button 
                className={styles.connectBtn}
                onClick={handlePatSubmit}
                disabled={!patValue || loadingApp === showPatModal.id}
                type="button"
              >
                {loadingApp === showPatModal.id ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu for Disconnect */}
      {showContextMenu && (
        <div 
          className={styles.contextMenu}
          style={{ 
            position: 'fixed', 
            top: showContextMenu.y, 
            left: showContextMenu.x,
            zIndex: 1000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className={styles.contextMenuItem}
            onClick={() => handleDisconnect(showContextMenu.app)}
            type="button"
          >
            <X size={14} />
            Disconnect {showContextMenu.app.name}
          </button>
        </div>
      )}
    </div>
  );
};

export default IntegrationApps;
